import React, { useEffect, useState } from 'react'
import { TouchableOpacity, View, ViewStyle, TextStyle, ActivityIndicator } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router'
import { Controller, useForm } from 'react-hook-form'
import { Avatar } from '@trivie/ui-web/components/avatar'
import { Header, color, Text, spacing, iconArrowBack } from '@trivie/ui-mobile'
import { useTranslation } from 'react-i18next'
import { BLOCKED_FILE_EXTENSIONS, useStores, validateAndFormatNumber } from '@trivie/core'
import { TextField } from '@mui/material'
import { useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'

export const AccountScreen = observer(() => {
  const { userStore, subscriptionStore, uiStore } = useStores()
  const { user, getUser, updateUser, uploadProfilePhoto, accessCode, reportProblem } = userStore

  const history = useHistory()
  const goBack = () => history.goBack()

  const { t } = useTranslation()
  const confirm = useConfirm()

  const formatPhone = (value?: string | null) => (value ? validateAndFormatNumber(value) || '' : '')

  const [first, setFirst] = useState(user?.first_name ?? '')
  const [last, setLast] = useState(user?.last_name ?? '')
  const [mobile, setMobile] = useState(formatPhone(user?.phone_number))
  const [touchedFirst, setTouchedFirst] = useState(false)
  const [touchedLast, setTouchedLast] = useState(false)
  const [touchedMobile, setTouchedMobile] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (user) getUser(user.id)
  }, [])

  useEffect(() => {
    if (!user) return
    setFirst(user.first_name ?? '')
    setLast(user.last_name ?? '')
    setMobile(formatPhone(user.phone_number))
  }, [user?.id])

  const { control } = useForm({ shouldUnregister: false })
  const handleFocusClick = (e: any) => {
    if (e.key === 'Enter') {
      onPressAvatar()
    }
  }

  const onPressAvatar = () => {
    const element = document.getElementById('divAvatar')
    if (element?.firstElementChild instanceof HTMLElement) {
      element.firstElementChild?.click()
    }
  }

  const openDeleteDialog = () => {
    confirm({
      title: t('Settings.Account.deleteAccount'),
      dialogText: t('Settings.Account.deletionMessage'),
      confirmationText: t('Settings.Account.yesDelete'),
      cancellationText: 'Cancel',
      danger: true,
    })
      .then(() => {
        reportProblem(
          `NAME: ${user?.first_name} ${user?.last_name}; EMAIL: ${user?.email}; ID: ${user?.id} ACCESS CODE: ${accessCode} has requested that their account be deleted`,
          'New Support Message',
        )
        confirm({
          title: t('Settings.Account.deleteAccount'),
          dialogText: t('Settings.Account.confirmDeletion', {
            support: process.env.REACT_APP_SUPPORT,
          }),
          confirmationText: 'OK',
          hideCancelButton: true,
        }).catch(() => {})
      })
      .catch(() => {
        /* user cancelled */
      })
  }

  const firstTrimmed = first.trim()
  const lastTrimmed = last.trim()
  const hadFirst = Boolean(user?.first_name?.trim())
  const hadLast = Boolean(user?.last_name?.trim())
  const originalFirst = user?.first_name ?? ''
  const originalLast = user?.last_name ?? ''
  const originalMobile = formatPhone(user?.phone_number)

  const firstError = hadFirst
    ? firstTrimmed === ''
      ? "First name can't be empty"
      : ''
    : first !== '' && firstTrimmed === ''
      ? 'Enter a first name'
      : ''

  const lastError = hadLast
    ? lastTrimmed === ''
      ? "Last name can't be empty"
      : ''
    : last !== '' && lastTrimmed === ''
      ? 'Enter a last name'
      : ''

  const mobileError =
    mobile.length > 0 && !validateAndFormatNumber(mobile) ? 'Enter a valid phone number' : error

  const hasChanges =
    firstTrimmed !== originalFirst.trim() ||
    lastTrimmed !== originalLast.trim() ||
    mobile !== originalMobile

  const isValid = !firstError && !lastError && !mobileError && hasChanges

  const onSave = () => {
    ;(document.activeElement as HTMLElement | null)?.blur()

    const payload: Record<string, any> = {
      phone_number: mobile.length > 0 ? validateAndFormatNumber(mobile) || null : null,
    }
    if (firstTrimmed && firstTrimmed !== originalFirst) {
      payload.first_name = firstTrimmed
    }
    if (lastTrimmed && lastTrimmed !== originalLast) {
      payload.last_name = lastTrimmed
    }

    updateUser(payload).then((d: any) => {
      if (d && d.message) {
        setError(d.message)
        confirm({
          title: 'Update failed',
          dialogText: d.message,
          confirmationText: 'OK',
          hideCancelButton: true,
          danger: true,
        }).catch(() => {})
      } else {
        setTouchedFirst(false)
        setTouchedLast(false)
        setTouchedMobile(false)
        setError('')
        confirm({
          title: 'Profile updated',
          dialogText: 'Your account details have been saved.',
          confirmationText: 'OK',
          hideCancelButton: true,
        }).catch(() => {})
      }
    })
  }

  const showUploadError = (message: string) => {
    confirm({
      title: 'Upload failed',
      dialogText: message,
      confirmationText: 'OK',
      hideCancelButton: true,
      danger: true,
    }).catch(() => {})
  }

  const isSubscriptionWorkspace = userStore.isSubscriptionWorkspace
  const hasActive = subscriptionStore.hasActiveSubscriptions

  return (
    <View style={FULL}>
      <div style={CONTAINER}>
        <Header
          leftIcon={iconArrowBack}
          onLeftPress={goBack}
          center
          headerTextStyle={{ ...HEADER, marginLeft: 14 }}
          headerTx="Settings.Account.header"
          rightElement={
            isSubscriptionWorkspace && !hasActive ? (
              <div style={{ width: 40 }} />
            ) : (
              <TouchableOpacity
                // @ts-ignore
                dataSet={{ qat: `save_btn` }}
                disabled={!isValid}
                onPress={onSave}
              >
                <Text
                  style={{ color: isValid ? color.blue : color.shade50 }}
                  variant="titleEmphasized"
                  tx="Common.save"
                />
              </TouchableOpacity>
            )
          }
        />
        <View
          style={PROFILE_CONTAINER}
          accessibilityLabel={`${user?.first_name} ${user?.last_name}`}
        >
          <View style={USER_CONTAINER}>
            {userStore.userLoaded === 'pending' ? (
              <View style={LOADER_CONTAINER}>
                <ActivityIndicator color="#999" size="large" />
              </View>
            ) : (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <div
                  role="button"
                  aria-label="choose profile picture"
                  id="divAvatar"
                  tabIndex={0}
                  onKeyDown={handleFocusClick}
                  style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                >
                  <Controller
                    control={control}
                    render={({ value }) => (
                      <Avatar
                        fontStyle={{ fontFamily: uiStore.branding.font_family, fontSize: 32 }}
                        size={90}
                        name={`${user?.first_name} ${user?.last_name}`}
                        url={value ? URL.createObjectURL(value) : user?.photo_url}
                        onChange={
                          isSubscriptionWorkspace && !hasActive
                            ? null
                            : (file: File) => {
                                if (file) {
                                  if (BLOCKED_FILE_EXTENSIONS.includes(file.type)) {
                                    let targetStr = file.type
                                    if (targetStr.includes('/')) {
                                      targetStr = targetStr.split('/')[1]
                                      showUploadError(
                                        `Sorry, but we don't support ${targetStr} images`,
                                      )
                                    } else {
                                      showUploadError(
                                        `Sorry, but we don't support ${file.type} files`,
                                      )
                                    }
                                  } else {
                                    const FILE_MAX_SIZE = 10 * 1024 * 1024
                                    if (file.size > FILE_MAX_SIZE) {
                                      showUploadError(
                                        `File size limit exceeded. Please select a file up to 10 MB.`,
                                      )
                                      return
                                    }
                                    const formData = new FormData()
                                    formData.append('photo', file)
                                    uploadProfilePhoto(formData, user!.id)
                                  }
                                }
                              }
                        }
                      />
                    )}
                    name="photo"
                    defaultValue=""
                  />
                </div>
                {isSubscriptionWorkspace && !hasActive ? null : (
                  <Text
                    // @ts-ignore
                    dataSet={{ qat: `change_profile_photo_btn` }}
                    onPress={onPressAvatar}
                    variant="body1Emphasized"
                    tx="Settings.Account.changePhoto"
                    style={{ marginTop: 20, color: color.blue }}
                  />
                )}
              </div>
            )}
          </View>
        </View>
        <View>
          <TextField
            variant="standard"
            label="First Name"
            value={first}
            onChange={(e) => {
              setTouchedFirst(true)
              setFirst(e.target.value)
            }}
            error={touchedFirst && Boolean(firstError)}
            helperText={touchedFirst && firstError ? firstError : ''}
            style={INPUT}
          />
          <TextField
            variant="standard"
            label="Last Name"
            value={last}
            onChange={(e) => {
              setTouchedLast(true)
              setLast(e.target.value)
            }}
            error={touchedLast && Boolean(lastError)}
            helperText={touchedLast && lastError ? lastError : ''}
            style={INPUT}
          />
          <TextField
            disabled
            variant="standard"
            label={t('Settings.Account.email')}
            value={user?.email ?? ''}
            style={INPUT}
          />
          <TextField
            // @ts-ignore
            dataSet={{ qat: `input_phone_number` }}
            variant="standard"
            label={t('Settings.Account.mobile')}
            onChange={(e) => {
              setTouchedMobile(true)
              setMobile(e.target.value)
            }}
            disabled={isSubscriptionWorkspace && !hasActive}
            error={(touchedMobile || Boolean(error)) && Boolean(mobileError)}
            helperText={(touchedMobile || Boolean(error)) && mobileError ? mobileError : ''}
            value={mobile}
            style={INPUT}
          />
          <Text
            // @ts-ignore
            dataSet={{ qat: `delete_account_btn` }}
            onPress={openDeleteDialog}
            variant="body1Emphasized"
            tx="Settings.Account.deleteAccount"
            style={{ marginTop: 20, fontSize: 17, color: color.red }}
          />
        </View>
      </div>
    </View>
  )
})

/**
 * Styling
 */
const FULL: ViewStyle = { flex: 1 }
const CONTAINER: React.CSSProperties = {
  backgroundColor: color.white,
}
const HEADER: TextStyle = { fontSize: 24, color: color.black }
const PROFILE_CONTAINER: ViewStyle = {
  marginTop: 20,
  marginBottom: spacing[7],
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'center',
}
const LOADER_CONTAINER: ViewStyle = {
  height: 100,
  width: 100,
  flexDirection: 'column',
  justifyContent: 'center',
  marginVertical: 16.5,
}
const USER_CONTAINER: ViewStyle = {
  alignItems: 'center',
  flexDirection: 'column',
  alignSelf: 'center',
  justifyContent: 'center',
}
const INPUT: React.CSSProperties = { marginBottom: 20 }
